import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Lock, Mail, Eye, EyeOff, ShieldCheck, ArrowLeft, Smartphone, ExternalLink } from 'lucide-react';
import useAxiosAuth from '../hooks/useAxiosAuth';
import { sendOtp, activateAccount } from '../redux/authSlice';
import { policyFor, isAnyOneContact } from '../utils/loginPolicy';
import baseURL from '../utils/baseURL';
import { PATIENT_WEB_URL } from '../utils/patientWebUrl';
import logo from '../assets/img/logo-TT2.png';

// Activation flow for pending accounts (bulk-uploaded patients, staff-created
// patients whose OTP was skipped at the desk, email-less family members). They
// arrive here from the login page after the backend reports
// `verificationRequired` (which now names the tenant). WHICH codes are needed
// is that hospital's login policy; the codes are submitted WITH the activation
// request and verified server-side — there is no separate verify step any
// more, because that is what forced the old (unsafe) client-asserted bypass.
//
// THE STAFF SIDE TOO ("Concept 2", owner, 1 Oct 2026): a doctor, a staff
// member or an ADMIN created by someone else comes through this door at their
// first sign-in. Their 403 carries two more things, both kept in the route
// state by Login.jsx:
//   - `activationToken` — sent back on /auth/activate; the server refuses the
//     staff side without it (ACTIVATION_TOKEN_REQUIRED → back to sign-in);
//   - `verify: { email, phone }` — EXACTLY which codes the server demands. When
//     it is there it is the only authority; the local policy mirror below is a
//     fallback for the answers that do not carry it (patients, older servers).
export default function ActivateAccount() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const axiosInstance = useAxiosAuth();

  const [email] = useState(location.state?.email || '');
  const [phone] = useState(location.state?.phone || '');
  const [tenantId] = useState(location.state?.tenantId || '');
  const [activationToken] = useState(location.state?.activationToken || '');
  const [serverVerify] = useState(() => {
    const v = location.state?.verify;
    return v && typeof v === 'object' ? { email: v.email === true, phone: v.phone === true } : null;
  });
  const [step, setStep] = useState(1); // 1 = send codes, 2 = enter codes + new password
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [phoneOtpCode, setPhoneOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);
  // A MOC PATIENT IDENTITY AT THE PROVIDER DOOR. /auth/activate answers 403
  // USE_MOC_PORTAL when the identifier is an active Mitra One Care patient
  // account with no users row here — there is nothing to activate, the
  // account already exists on the patient portal. Same panel as Login's, and
  // it REPLACES the form, because every control on it is now a dead end.
  const [mocPortal, setMocPortal] = useState(false);

  // The hospital's login policy decides which channels must verify — mirrors
  // the server-side rule in authController.activateAccount exactly.
  const [publicTenants, setPublicTenants] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Signed out, so /tenants/public answers only the hospital named —
        // by id here (the activation link knows it), policy without the name.
        const res = await axiosInstance.get(`${baseURL}/tenants/public`, tenantId ? { params: { tenantId } } : undefined);
        if (alive) setPublicTenants(res.data?.tenants || []);
      } catch {
        if (alive) setPublicTenants([]);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const policy = policyFor(publicTenants, tenantId);
  let needEmail = !!(policy.email.otp && email);
  let needPhone = !!(policy.mobile.otp && phone);
  // The server said exactly which codes it wants — take that, nothing else.
  // (Fallback rule below only when `verify` is absent.)
  if (serverVerify) {
    needEmail = !!(serverVerify.email && email);
    needPhone = !!(serverVerify.phone && phone);
  }
  // ONE CODE, ON THE CHANNEL THIS HOSPITAL MANDATES (owner, 18 Sep 2026:
  // "Activation: one OTP on the mobile"). Under the mobile-first default the
  // e-mail carries otp:true so that it is verified WHEN GIVEN — at
  // registration, with the patient in front of the form — not so that an
  // optional contact can block the account at its front door. So when exactly
  // one channel is `required`, that is the one asked for here. 'both' mandates
  // everything and still asks for both; 'either' has no primary channel and is
  // handled by its own rule below. Mirrors authController.activateAccount.
  if (!serverVerify && needEmail && needPhone && !isAnyOneContact(policy)) {
    if (policy.mobile.required && !policy.email.required) needEmail = false;
    else if (policy.email.required && !policy.mobile.required) needPhone = false;
  }
  // The server ALSO drops a channel whose flag is already true (a desk-proved
  // e-mail beside an unproved mobile). That one cannot be mirrored: the 403
  // `verificationRequired` body carries the two contacts but not their verified
  // flags. It only ever asks for FEWER codes than this screen collects, so the
  // extra field is a wasted keystroke, never a failed activation — the server
  // simply ignores a code for a channel it did not ask about.
  //
  // 'either' — ANY ONE contact, verified: one code claims the account even
  // when it carries both. The e-mail is the channel asked for, because it is
  // the one with a real delivery provider today. Mirrors the server's rule in
  // authController.activateAccount.
  if (!serverVerify && isAnyOneContact(policy) && needEmail && needPhone) needPhone = false;
  if (!serverVerify && !needEmail && !needPhone) {
    if (email) needEmail = true;
    else if (phone) needPhone = true;
  }
  // The server asked for no code at all (every contact already proved): the
  // only thing left is the new password, so the code step is skipped.
  const noCodes = !!serverVerify && !needEmail && !needPhone;
  const currentStep = noCodes ? 2 : step;

  // Resend cooldown ticker.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    timerRef.current = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(timerRef.current);
  }, [resendIn]);

  const handleSendCode = async () => {
    if (!needEmail && !needPhone) {
      toast.warning(t('activate.no_channels', 'This account has no contact details to verify. Please contact your hospital.'), { autoClose: 3000 });
      return;
    }
    setSending(true);
    try {
      await Promise.all([
        ...(needEmail ? [dispatch(sendOtp({ email, axiosInstance })).unwrap()] : []),
        ...(needPhone ? [dispatch(sendOtp({ phone, axiosInstance })).unwrap()] : []),
      ]);
      toast.success(
        needEmail && needPhone
          ? t('activate.code_sent', 'Verification codes have been sent to your email and phone.')
          : needEmail
            ? t('activate.code_sent_email', 'A verification code has been sent to your email.')
            : t('activate.code_sent_phone', 'A verification code has been sent to your phone.'),
        { autoClose: 2500 }
      );
      setStep(2);
      setResendIn(60);
    } catch (err) {
      toast.warning(typeof err === 'string' ? err : t('activate.send_failed', 'Failed to send verification codes'), { autoClose: 2500 });
    } finally {
      setSending(false);
    }
  };

  const handleActivate = async () => {
    if (needEmail && emailOtpCode.trim().length < 4) {
      toast.warning(t('activate.enter_email_code', 'Enter the email verification code'), { autoClose: 2000 });
      return;
    }
    if (needPhone && phoneOtpCode.trim().length < 4) {
      toast.warning(t('activate.enter_phone_code', 'Enter the mobile verification code'), { autoClose: 2000 });
      return;
    }
    if (newPassword.length < 8) {
      toast.warning(t('activate.password_short', 'Password must be at least 8 characters'), { autoClose: 2000 });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.warning(t('activate.password_mismatch', 'Passwords do not match'), { autoClose: 2000 });
      return;
    }
    setSubmitting(true);
    try {
      const done = await dispatch(activateAccount({
        identifier: email || phone,
        tenantId: tenantId || null,
        ...(needEmail ? { emailOtp: emailOtpCode.trim() } : {}),
        ...(needPhone ? { phoneOtp: phoneOtpCode.trim() } : {}),
        newPassword,
        ...(activationToken ? { activationToken } : {}),
        axiosInstance,
      })).unwrap();
      toast.success(t('activate.success', 'Account verified. You are now signed in.'), { autoClose: 2500 });
      // Whoever was activated lands on THEIR home. This used to send every
      // role to the patient dashboard — a doctor activating an account on the
      // doctor site was bounced off a route they may not open (30 Sep 2026).
      const home = {
        doctor: '/doctorDashboard', admin: '/adminDashboard', super_admin: '/adminDashboard',
        user: '/staffDashboard', patient: '/patientDashboard',
      }[done?.user?.role];
      navigate(home || '/', { replace: true });
    } catch (err) {
      if (err && err.code === 'USE_MOC_PORTAL') {
        setMocPortal(true);
        return;
      }
      // The staff-side door wants the token the sign-in handed out (missing
      // after a reload, or expired). Only a fresh sign-in mints a new one.
      if (err && err.code === 'ACTIVATION_TOKEN_REQUIRED') {
        toast.warning(
          t('activate.token_required', 'Please sign in again to activate your account.'),
          { autoClose: 3500 }
        );
        navigate('/login', { replace: true });
        return;
      }
      // A structured refusal still carries the server's localised sentence —
      // show that rather than the generic fallback.
      toast.warning(
        typeof err === 'string' ? err : (err?.message || t('activate.activate_failed', 'Activation failed')),
        { autoClose: 2500 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-4 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B]/90">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
        <div className="flex flex-col items-center mb-6">
          <img src={logo} alt="Mitra One Care" className="h-12 mb-3" />
          <div className="flex items-center gap-2 text-[#3AA8A8]">
            <ShieldCheck className="w-5 h-5" />
            <h1 className="text-xl font-bold">{t('activate.title', 'Verify Your Account')}</h1>
          </div>
          <p className="text-sm text-gray-500 mt-2 text-center">
            {mocPortal
              ? t('login.moc_portal_title')
              : currentStep === 1
                ? t('activate.subtitle_request', 'Generate your verification code(s) first.')
                : t('activate.subtitle_activate', 'Enter the code(s) you received and choose your new password.')}
          </p>
        </div>

        {/* 403 USE_MOC_PORTAL — nothing on this screen can activate a Mitra One
            Care patient account, so the form goes away and the way out takes
            its place. Same copy as Login's panel (login.moc_portal_*): one
            sentence, translated once, for the one situation. The button only
            exists when this deployment HAS a patient site. */}
        {mocPortal && (
          <div
            data-testid="activate-moc-portal"
            className="rounded-xl border border-[#4ABDBD] bg-[#E7F5F4] p-4 text-sm text-gray-700"
          >
            <p>{t('login.moc_portal_body')}</p>
            {PATIENT_WEB_URL && (
              /* A real <a>: the patient portal is another deployment, so this
                 is an external link and has to behave like one. */
              <a
                href={`${PATIENT_WEB_URL}/#/login`}
                data-testid="activate-moc-portal-cta"
                className="mt-3 inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] px-3 py-2 text-sm font-semibold text-white transition hover:shadow-lg"
              >
                {t('login.moc_portal_cta')} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        )}

        {!mocPortal && email ? (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.email', 'Email')}</label>
            <div className="relative mb-3">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={email}
                disabled
                readOnly
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl bg-gray-100 text-gray-600"
              />
            </div>
          </>
        ) : null}

        {!mocPortal && phone ? (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.phone', 'Mobile Number')}</label>
            <div className="relative mb-4">
              <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={phone}
                disabled
                readOnly
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl bg-gray-100 text-gray-600"
              />
            </div>
          </>
        ) : null}

        {!mocPortal && currentStep === 1 && (
          <>
            <div className="rounded-xl border border-[#D7EEED] bg-[#F5FCFC] p-4 mb-4 text-sm text-gray-700">
              {needEmail && needPhone
                ? t('activate.step1_hint_both', 'Your hospital requires both your email and your mobile number to be verified. Generate the codes to begin.')
                : needPhone
                  ? t('activate.step1_hint_phone', 'Your hospital requires your mobile number to be verified. Generate the code to begin.')
                  : t('activate.step1_hint_email', 'Your email address needs to be verified. Generate the code to begin.')}
              {/* One code, even though the account carries both contacts —
                  say why, or the unused field reads like a bug. */}
              {needPhone && !needEmail && email && (
                <span className="block mt-2 text-xs text-gray-500" data-testid="activate-single-channel-note">
                  {t('activate.single_channel_note', 'Only your mobile number needs a code here — you can verify your email later from your profile.')}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleSendCode}
              disabled={sending || (!needEmail && !needPhone)}
              className={`w-full py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition ${sending || (!needEmail && !needPhone) ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg'}`}
            >
              {sending ? t('activate.sending', 'Sending…') : t('activate.generate_otp', 'Generate OTP')}
            </button>
          </>
        )}

        {!mocPortal && currentStep === 2 && (
          <>
            <div data-testid="activate-step2-hint" className="rounded-xl border border-[#D7EEED] bg-[#F5FCFC] p-4 mb-4 text-sm text-gray-700">
              {noCodes
                ? t('activate.password_only_hint', 'Your contact details are already verified. Choose your new password to finish.')
                : t('activate.step2_hint', 'The code(s) are checked when you activate — enter them together with your new password.')}
            </div>

            {needEmail && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.email_code', 'Email OTP')}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={emailOtpCode}
                  onChange={(e) => setEmailOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  className="w-full mb-4 px-3 py-2.5 tracking-[0.4em] text-center border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                />
              </>
            )}

            {needPhone && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.phone_code', 'Mobile OTP')}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={phoneOtpCode}
                  onChange={(e) => setPhoneOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                  className="w-full mb-4 px-3 py-2.5 tracking-[0.4em] text-center border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                />
              </>
            )}

            <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.new_password', 'New Password')}</label>
            <div className="relative mb-4">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('activate.new_password', 'New Password')}
                className="w-full pl-9 pr-10 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
              />
              <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">{t('activate.confirm_password', 'Confirm Password')}</label>
            <div className="relative mb-4">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('activate.confirm_password', 'Confirm Password')}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
              />
            </div>

            <button
              type="button"
              onClick={handleActivate}
              disabled={submitting}
              className={`w-full py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] transition ${submitting ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg'}`}
            >
              {submitting ? t('activate.activating', 'Verifying…') : t('activate.verify_activate', 'Verify & Activate')}
            </button>

            {!noCodes && (
            <div className="text-center mt-4 text-sm">
              <button
                type="button"
                onClick={handleSendCode}
                disabled={resendIn > 0 || sending}
                className={`text-[#3AA8A8] font-medium ${resendIn > 0 || sending ? 'opacity-50 cursor-not-allowed' : 'hover:underline'}`}
              >
                {resendIn > 0
                  ? t('activate.resend_in', 'Resend code in {{s}}s', { s: resendIn })
                  : t('activate.resend', 'Resend code')}
              </button>
            </div>
            )}
          </>
        )}

        <button
          type="button"
          onClick={() => navigate('/login')}
          className="mt-6 w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="w-4 h-4" /> {t('activate.back_to_login', 'Back to login')}
        </button>
      </div>
    </div>
  );
}
